import { Button, FormControl, FormLabel, Input, Text } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { checkEmailDuplicate } from "../../../api";
import { colors } from "../../../styles";   
import { ISignUpUser } from "../../../types";
import { getApiErrorCode } from "../../../utils";
import { getSignUpErrorMessage } from "./errors";
import StepLayout, { FieldMessage, underlineInputProps } from "./StepLayout";

interface IUserInfoProps {
    progress: number;
    setUserData: React.Dispatch<React.SetStateAction<Partial<ISignUpUser>>>;
    onNext: () => void;
}

interface IUserInfoForm {
    name: string;
    email: string;
    password: string;
    passwordCheck: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&~])[A-Za-z\d@$!%*#?&~]{8,20}$/;

/* 모든 유형 공통. User 객체(이름, 이메일, 비밀번호)를 만든다. */

export default function UserInfo({ progress, setUserData, onNext }: IUserInfoProps) {

    /* 로딩 상태, 에러 메시지, 디바운스된 이메일 */

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [debouncedEmail, setDebouncedEmail] = useState("");

    /* useForm */

    const { register, handleSubmit, watch } = useForm<IUserInfoForm>();

    const name = watch("name") ?? "";
    const email = watch("email") ?? "";
    const password = watch("password") ?? "";
    const passwordCheck = watch("passwordCheck") ?? "";

    /* 이메일 형식 검사 */

    const isEmailFormatValid = emailRegex.test(email);

    /* 비밀번호 형식 검사 */

    const isPasswordFormatValid = passwordRegex.test(password);

    /* 비밀번호 일치 검사 */

    const isPasswordMatched = password !== "" && password === passwordCheck;

    /* 디바운스된 이메일 */

    useEffect(() => {
        const id = setTimeout(() => setDebouncedEmail(email), 400);
        return () => clearTimeout(id);
    }, [email]);

    /* 이메일 중복 확인 쿼리 */

    const { isLoading: isCheckingEmail, isError: isEmailCheckError, error: emailCheckError } = useQuery({
        queryKey: ["checkEmailDuplicate", debouncedEmail],
        queryFn: () => checkEmailDuplicate({ email: debouncedEmail }),
        enabled: !!debouncedEmail && emailRegex.test(debouncedEmail),
        retry: false,
    });

    /* 이메일 중복 확인 결과 */

    const isEmailDuplicate = isEmailCheckError && getApiErrorCode(emailCheckError) === "EMAIL_ALREADY_EXISTS";
    const isEmailCheckUnavailable = isEmailCheckError && !isEmailDuplicate;

    /* 이메일 중복 확인 뮤테이션 */

    const checkEmailDuplicateMutation = useMutation({
        mutationFn: checkEmailDuplicate,
        retry: false,
    });

    /* 폼 제출 핸들러 */

    const onSubmit = async (data: IUserInfoForm) => {

        /* 로딩 상태, 에러 메시지 설정 */

        setIsLoading(true);
        setErrorMessage("");

        try {

            /* 이메일 중복 확인 */

            await checkEmailDuplicateMutation.mutateAsync({ email: data.email });
            
            /* 공통 User 필드만 저장. 면허/재학증명/약국은 이후 단계에서 따로 모은다. */

            setUserData((prev) => ({
                ...prev,
                name: data.name,
                email: data.email,
                password: data.password,
            }));

            /* 다음 화면으로 이동 */

            onNext();
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "이메일 중복 확인 중 문제가 발생했습니다."));
        } finally {
            setIsLoading(false);
        }
    };

    /* 이메일 힌트 */

    const emailHint = (() => {
        if (!email) return null;
        if (!isEmailFormatValid) {
            return { message: "이메일 형식이 올바르지 않습니다.", tone: "error" as const };
        }
        if (isCheckingEmail) {
            return { message: "중복 확인 중...", tone: "muted" as const };
        }
        if (isEmailDuplicate) {
            return { message: "이미 사용 중인 이메일입니다.", tone: "error" as const };
        }
        if (isEmailCheckUnavailable) {
            return { message: "이메일 중복 확인에 실패했습니다.", tone: "error" as const };
        }
        return { message: "사용 가능한 이메일입니다.", tone: "success" as const };
    })();

    /* render */

    return (
        <StepLayout
            progress={progress}
            titles={["회원 정보를 입력해주세요."]}
            asForm
            onSubmit={handleSubmit(onSubmit)}
        >

            {/* 이름 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>이름</FormLabel>
                <Input
                    {...register("name", { required: true })}
                    type="text"
                    placeholder="이름 입력"
                    {...underlineInputProps}
                />
            </FormControl>

            {/* 이메일 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>이메일 주소</FormLabel>
                <Input
                    {...register("email", { required: true })}
                    type="text"
                    placeholder="팜챗에 가입하실 이메일 입력"
                    {...underlineInputProps}
                />
                <FieldMessage message={emailHint?.message} tone={emailHint?.tone} />
            </FormControl>

            {/* 비밀번호 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>비밀번호</FormLabel>
                <Input
                    {...register("password", { required: true })}
                    type="password"
                    placeholder="비밀번호 입력"
                    {...underlineInputProps}
                />
            </FormControl>
            <Text color={password && !isPasswordFormatValid ? "red.500" : colors.g3} textAlign="start" fontSize="xs">
                비밀번호는 영문, 숫자, 특수문자를 포함한 8자 이상이어야 합니다.
            </Text>

            {/* 비밀번호 확인 입력 폼 */}

            <FormControl marginTop={5}>
                <FormLabel fontSize="sm" color={colors.blue}>비밀번호 확인</FormLabel>
                <Input
                    {...register("passwordCheck", { required: true })}
                    type="password"
                    placeholder="비밀번호 확인"
                    {...underlineInputProps}
                />
                {passwordCheck && !isPasswordMatched && (
                    <FieldMessage message="비밀번호가 일치하지 않습니다." />
                )}
            </FormControl>

            {/* 에러 메시지 */}

            <FieldMessage message={errorMessage} />

            {/* 다음 버튼 */}

            <Button
                type="submit"
                width="100%"
                height="52px"
                backgroundColor={colors.blue}
                color="white"
                borderRadius={10}
                marginTop={10}
                isLoading={isLoading}
                disabled={
                    !name.trim() ||
                    !isEmailFormatValid ||
                    isCheckingEmail ||
                    isEmailCheckError ||
                    !isPasswordFormatValid ||
                    !isPasswordMatched
                }
            >
                <Text fontSize={16} fontWeight={700}>다음</Text>
            </Button>
        </StepLayout>
    );
}
